import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import {
  Loader2, Plus, Trash2, Star, ShieldCheck, AlertTriangle, Building2, Wallet,
  CheckCircle2, Clock, XCircle,
} from "lucide-react";

/**
 * CARTAL — Seller Payout / IBAN Management page (`/payouts`).
 * Additive; does not modify any existing dashboard.
 */
export default function PayoutAccountsPage() {
  const { lang } = useI18n();
  const [accounts, setAccounts] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showWd, setShowWd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [a, s, w] = await Promise.all([
        apiClient.get("/payouts/accounts"),
        apiClient.get("/payouts/summary"),
        apiClient.get("/payouts/withdrawals"),
      ]);
      setAccounts(a.data.items || []);
      setSummary(s.data);
      setWithdrawals(w.data.items || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-10" data-testid="payouts-page">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline overline-gold">{lang === "ar" ? "المدفوعات والسحوبات" : "PAYOUTS"}</div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-[#0a0a0a]">
            {lang === "ar" ? "حسابات الاستلام والسحب" : "Payout accounts & withdrawals"}
          </h1>
          <p className="text-sm text-[#6b6b6b] mt-1 max-w-2xl">
            {lang === "ar"
              ? "أضف آيبان بنكك السعودي لاستلام مبيعاتك من كارتال. يتم توثيق كل حساب من قبل الإدارة قبل السحب."
              : "Add your Saudi IBAN to receive your CARTAL earnings. Each account is verified by admin before withdrawals are allowed."}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a]"
            data-testid="payouts-add"
          >
            <Plus className="w-4 h-4" />
            {lang === "ar" ? "إضافة آيبان" : "Add IBAN"}
          </button>
          <button
            onClick={() => setShowWd(true)}
            disabled={!summary?.can_withdraw}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold border-2 border-[#c9a55c] text-[#8a6f2e] hover:bg-[#f5f0e6] disabled:opacity-40 disabled:cursor-not-allowed"
            title={!summary?.can_withdraw ? (lang === "ar" ? "تحتاج حساباً موثّقاً" : "Verified account required") : ""}
            data-testid="payouts-withdraw"
          >
            <Wallet className="w-4 h-4" />
            {lang === "ar" ? "طلب سحب" : "Request withdrawal"}
          </button>
        </div>
      </div>

      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard label={lang === "ar" ? "الحسابات" : "Accounts"}
                       value={summary.accounts_total} icon={<Building2 className="w-4 h-4" />} />
          <SummaryCard label={lang === "ar" ? "موثّقة" : "Verified"}
                       value={summary.accounts_verified} icon={<ShieldCheck className="w-4 h-4" />}
                       tone={summary.accounts_verified > 0 ? "green" : "gold"} />
          <SummaryCard label={lang === "ar" ? "طلبات معلّقة" : "Pending withdrawals"}
                       value={summary.pending_withdrawals} icon={<Clock className="w-4 h-4" />} />
          <SummaryCard label={lang === "ar" ? "الافتراضي" : "Default IBAN"}
                       value={summary.default_iban_masked || "—"} icon={<Star className="w-4 h-4" />} />
        </div>
      )}

      {err && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 mb-4">{err}</div>
      )}

      {/* Accounts list */}
      <section className="mb-10">
        <h2 className="font-display text-xl font-medium text-[#0a0a0a] mb-4">
          {lang === "ar" ? "الحسابات البنكية" : "Bank accounts"}
        </h2>
        {loading ? (
          <div className="py-16 text-center text-[#6b6b6b]"><Loader2 className="w-5 h-5 animate-spin inline me-2" />{lang === "ar" ? "تحميل…" : "Loading…"}</div>
        ) : accounts.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#c9a55c]/40 py-16 text-center">
            <p className="text-[#6b6b6b] text-sm">{lang === "ar" ? "لا توجد حسابات بعد." : "No accounts yet."}</p>
            <button onClick={() => setShowAdd(true)} className="mt-3 text-sm font-bold text-[#8a6f2e] hover:text-[#c9a55c]">
              {lang === "ar" ? "أضف أول آيبان →" : "Add your first IBAN →"}
            </button>
          </div>
        ) : (
          <div className="grid gap-3">
            {accounts.map((a) => (
              <AccountRow key={a.id} a={a} lang={lang} reload={load} />
            ))}
          </div>
        )}
      </section>

      {/* Withdrawals history */}
      <section>
        <h2 className="font-display text-xl font-medium text-[#0a0a0a] mb-4">
          {lang === "ar" ? "سجل السحوبات" : "Withdrawal history"}
        </h2>
        {withdrawals.length === 0 ? (
          <p className="text-sm text-[#8a8a8a]">{lang === "ar" ? "لا توجد سحوبات بعد." : "No withdrawals yet."}</p>
        ) : (
          <div className="rounded-2xl border border-[#0a0a0a]/10 bg-white overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#fafaf7] text-[10px] uppercase tracking-[0.2em] text-[#6b6b6b]">
                <tr>
                  <th className="text-start p-3">{lang === "ar" ? "التاريخ" : "Date"}</th>
                  <th className="text-start p-3">{lang === "ar" ? "المبلغ" : "Amount"}</th>
                  <th className="text-start p-3">{lang === "ar" ? "الآيبان" : "IBAN"}</th>
                  <th className="text-start p-3">{lang === "ar" ? "الحالة" : "Status"}</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((w) => (
                  <tr key={w.id} className="border-b border-[#0a0a0a]/5">
                    <td className="p-3 text-xs" dir="ltr">{w.created_at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="p-3 font-mono font-bold" dir="ltr">{Number(w.amount_sar).toLocaleString()} SAR</td>
                    <td className="p-3 font-mono text-xs" dir="ltr">{w.iban_masked}</td>
                    <td className="p-3"><StatusPill status={w.status} lang={lang} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="mt-10 text-center">
        <Link to="/dashboard" className="text-sm text-[#8a6f2e] hover:text-[#c9a55c]">
          ← {lang === "ar" ? "العودة إلى لوحة التحكم" : "Back to dashboard"}
        </Link>
      </div>

      {showAdd && <AddAccountModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} lang={lang} />}
      {showWd && <WithdrawalModal accounts={accounts.filter(a => a.status === "verified")} onClose={() => setShowWd(false)} onSaved={() => { setShowWd(false); load(); }} lang={lang} />}
    </div>
  );
}

function SummaryCard({ label, value, icon, tone = "gold" }) {
  const bg = tone === "green" ? "rgba(34,197,94,0.08)" : "rgba(201,165,92,0.08)";
  const fg = tone === "green" ? "#16a34a" : "#8a6f2e";
  return (
    <div className="rounded-2xl p-4 border" style={{ backgroundColor: bg, borderColor: fg + "44" }}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: fg }}>
        {icon}{label}
      </div>
      <div className="mt-1 font-mono text-xl font-bold text-[#0a0a0a] truncate" dir="ltr">{value}</div>
    </div>
  );
}

function StatusPill({ status, lang }) {
  const m = {
    pending:  { c: "#c9a55c", ar: "قيد المراجعة", en: "Pending" },
    verified: { c: "#22c55e", ar: "موثّق", en: "Verified" },
    rejected: { c: "#ef4444", ar: "مرفوض", en: "Rejected" },
    suspended:{ c: "#8b5cf6", ar: "معلّق", en: "Suspended" },
    approved: { c: "#3b82f6", ar: "معتمد", en: "Approved" },
    paid:     { c: "#22c55e", ar: "مصروف", en: "Paid" },
  };
  const s = m[status] || { c: "#8a8a8a", ar: status, en: status };
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
          style={{ backgroundColor: s.c + "22", color: s.c, border: `1px solid ${s.c}55` }}>
      {lang === "ar" ? s.ar : s.en}
    </span>
  );
}

function AccountRow({ a, lang, reload }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!window.confirm(lang === "ar" ? "حذف هذا الحساب؟" : "Delete this account?")) return;
    setBusy(true);
    try { await apiClient.delete(`/payouts/accounts/${a.id}`); await reload(); } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const makeDefault = async () => {
    setBusy(true);
    try { await apiClient.post(`/payouts/accounts/${a.id}/default`); await reload(); } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border-2 bg-white p-4 flex items-center justify-between gap-4 flex-wrap"
         style={{ borderColor: a.is_default ? "#c9a55c" : "#0a0a0a1a" }}
         data-testid={`payout-account-${a.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
             style={{ background: "linear-gradient(135deg,#c9a55c,#8a6f2e)" }}>
          <Building2 className="w-4 h-4 text-[#0a0a0a]" />
        </div>
        <div className="min-w-0">
          <div className="font-bold text-[#0a0a0a] flex items-center gap-2 flex-wrap">
            {a.bank_name}
            {a.is_default && <span className="text-[10px] font-bold text-[#8a6f2e] bg-[#fdf9ef] border border-[#c9a55c] rounded-full px-2 py-0.5">{lang === "ar" ? "افتراضي" : "Default"}</span>}
            <StatusPill status={a.status} lang={lang} />
          </div>
          <div className="text-xs text-[#525252] mt-0.5">{a.account_holder_name}</div>
          <div className="text-xs font-mono text-[#8a8a8a]" dir="ltr">{a.iban_masked}</div>
        </div>
      </div>
      <div className="flex gap-2">
        {a.status === "verified" && !a.is_default && (
          <button onClick={makeDefault} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold text-[#8a6f2e] hover:text-[#c9a55c]" data-testid={`account-set-default-${a.id}`}>
            <Star className="w-3.5 h-3.5" />{lang === "ar" ? "افتراضي" : "Default"}
          </button>
        )}
        <button onClick={remove} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold text-red-600 hover:text-red-700" data-testid={`account-delete-${a.id}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          {lang === "ar" ? "حذف" : "Remove"}
        </button>
      </div>
    </div>
  );
}

function AddAccountModal({ onClose, onSaved, lang }) {
  const [iban, setIban] = useState("");
  const [holder, setHolder] = useState("");
  const [setDefault, setSetDefault] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!iban || iban.replace(/\s+/g, "").length < 15) { setValidation(null); return; }
    setValidating(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await apiClient.get("/payouts/iban/validate", { params: { iban } });
        setValidation(data);
      } catch { /* noop */ }
      finally { setValidating(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [iban]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await apiClient.post("/payouts/accounts", {
        iban, account_holder_name: holder, set_default: setDefault,
      });
      onSaved();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ backgroundColor: "rgba(10,10,10,0.75)", backdropFilter: "blur(6px)" }}
         onClick={onClose}
         data-testid="add-account-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
        <h2 className="font-display text-xl font-medium text-[#0a0a0a] mb-1">
          {lang === "ar" ? "إضافة حساب بنكي (آيبان)" : "Add a bank account (IBAN)"}
        </h2>
        <p className="text-xs text-[#6b6b6b] mb-4">
          {lang === "ar" ? "سيتم توثيق الحساب من قبل الإدارة قبل السماح بالسحب." : "Admin will verify before withdrawals are enabled."}
        </p>
        <label className="text-xs text-[#6b6b6b] block">{lang === "ar" ? "آيبان سعودي (24 خانة)" : "Saudi IBAN (24 chars)"}</label>
        <input value={iban} onChange={(e) => setIban(e.target.value)}
               className="input-dark mt-1 font-mono uppercase" dir="ltr"
               placeholder="SA03 8000 0000 6080 1016 7519"
               data-testid="iban-input" required />
        {validating && <div className="text-[11px] text-[#8a8a8a] mt-1">{lang === "ar" ? "التحقق…" : "Validating…"}</div>}
        {validation && validation.valid && (
          <div className="mt-1 text-[11px] text-green-700 inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> {validation.bank_name} · <span dir="ltr">{validation.masked}</span>
          </div>
        )}
        {validation && !validation.valid && (
          <div className="mt-1 text-[11px] text-red-700 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {validation.error}
          </div>
        )}

        <label className="text-xs text-[#6b6b6b] block mt-4">{lang === "ar" ? "اسم صاحب الحساب" : "Account holder name"}</label>
        <input value={holder} onChange={(e) => setHolder(e.target.value)}
               className="input-dark mt-1" required minLength={3}
               data-testid="holder-input" />

        <label className="flex items-center gap-2 text-xs text-[#3a3a3a] mt-4">
          <input type="checkbox" checked={setDefault} onChange={(e) => setSetDefault(e.target.checked)} />
          {lang === "ar" ? "تعيين كحساب افتراضي (بعد التوثيق)" : "Set as default (after verification)"}
        </label>

        {err && <div className="mt-3 text-xs text-red-700">{err}</div>}

        <div className="mt-6 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="rounded-full px-4 py-2 text-sm font-bold text-[#0a0a0a] hover:bg-[#f5f0e6]">
            {lang === "ar" ? "إلغاء" : "Cancel"}
          </button>
          <button type="submit" disabled={busy || !validation?.valid}
                  className="rounded-full px-5 py-2 text-sm font-bold bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a] disabled:opacity-50"
                  data-testid="account-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "ar" ? "حفظ" : "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function WithdrawalModal({ accounts, onClose, onSaved, lang }) {
  const [amount, setAmount] = useState("");
  const [acctId, setAcctId] = useState(accounts[0]?.id || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await apiClient.post("/payouts/withdrawals", {
        amount_sar: Number(amount),
        payout_account_id: acctId,
        note: note || null,
      });
      onSaved();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ backgroundColor: "rgba(10,10,10,0.75)", backdropFilter: "blur(6px)" }}
         onClick={onClose}
         data-testid="withdrawal-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
        <h2 className="font-display text-xl font-medium text-[#0a0a0a] mb-1">
          {lang === "ar" ? "طلب سحب" : "Request withdrawal"}
        </h2>
        <p className="text-xs text-[#6b6b6b] mb-4">
          {lang === "ar"
            ? "سيتم مراجعة الطلب خلال 24-48 ساعة عمل. عند الموافقة يتم التحويل خلال 3-5 أيام."
            : "Reviewed within 24-48 business hours. Once approved, funds transfer within 3-5 days."}
        </p>

        <label className="text-xs text-[#6b6b6b] block">{lang === "ar" ? "الحساب البنكي" : "Bank account"}</label>
        <select value={acctId} onChange={(e) => setAcctId(e.target.value)} required className="input-dark mt-1" data-testid="wd-account">
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.bank_name} — {a.iban_masked}</option>
          ))}
        </select>

        <label className="text-xs text-[#6b6b6b] block mt-4">{lang === "ar" ? "المبلغ (ر.س)" : "Amount (SAR)"}</label>
        <input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
               className="input-dark mt-1" dir="ltr" required data-testid="wd-amount" />

        <label className="text-xs text-[#6b6b6b] block mt-4">{lang === "ar" ? "ملاحظة اختيارية" : "Note (optional)"}</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)}
                  rows={2} className="input-dark mt-1" />

        {err && <div className="mt-3 text-xs text-red-700">{err}</div>}

        <div className="mt-6 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="rounded-full px-4 py-2 text-sm font-bold text-[#0a0a0a] hover:bg-[#f5f0e6]">
            {lang === "ar" ? "إلغاء" : "Cancel"}
          </button>
          <button type="submit" disabled={busy || !acctId}
                  className="rounded-full px-5 py-2 text-sm font-bold bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a] disabled:opacity-50"
                  data-testid="wd-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "ar" ? "إرسال الطلب" : "Submit request")}
          </button>
        </div>
      </form>
    </div>
  );
}
